import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

const STORAGE_KEY = "compare:practices:v1";
const MAX_COMPARE = 3;

export interface ComparePractice {
  id: string;
  name: string;
  postcode?: string | null;
}

interface CompareContextValue {
  items: ComparePractice[];
  isInCompare: (id: string) => boolean;
  add: (p: ComparePractice) => boolean;
  remove: (id: string) => void;
  clear: () => void;
  full: boolean;
  max: number;
}

const CompareContext = createContext<CompareContextValue | null>(null);

export const CompareProvider = ({ children }: { children: ReactNode }) => {
  const [items, setItems] = useState<ComparePractice[]>(() => {
    if (typeof window === "undefined") return [];
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as ComparePractice[]) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      /* ignore quota / SSR */
    }
  }, [items]);

  // Sync selection across tabs / windows so the floating bar stays consistent.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return;
      try {
        const next = e.newValue ? (JSON.parse(e.newValue) as ComparePractice[]) : [];
        setItems(next);
      } catch {
        /* ignore malformed */
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const isInCompare = useCallback((id: string) => items.some((it) => it.id === id), [items]);

  const add = useCallback((p: ComparePractice) => {
    let added = false;
    setItems((prev) => {
      if (prev.some((it) => it.id === p.id)) return prev;
      if (prev.length >= MAX_COMPARE) return prev;
      added = true;
      return [...prev, p];
    });
    return added;
  }, []);

  const remove = useCallback((id: string) => setItems((prev) => prev.filter((it) => it.id !== id)), []);
  const clear = useCallback(() => setItems([]), []);

  const value = useMemo<CompareContextValue>(
    () => ({ items, isInCompare, add, remove, clear, full: items.length >= MAX_COMPARE, max: MAX_COMPARE }),
    [items, isInCompare, add, remove, clear],
  );

  return <CompareContext.Provider value={value}>{children}</CompareContext.Provider>;
};

export const useCompare = () => {
  const ctx = useContext(CompareContext);
  if (!ctx) throw new Error("useCompare must be used within CompareProvider");
  return ctx;
};
